import * as d3 from 'd3';
import dayjs from 'dayjs';

type TimeInterval = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'auto';

interface GenerateTimeTicksParams {
	chartWidth: number;
	tickFontSize: number;
	timeValues: Array<Date | string | number>;
	interval: TimeInterval;
	xTickFormat: (d: any) => string;
}

interface GenerateTimeTicksResult {
	ticks: Date[];
	suggestedInterval: Exclude<TimeInterval, 'null'>;
	suggestedRotation: 0 | 45;
}

/**
 * Generates time ticks for a chart based on available width, font size, and time interval
 */
export function generateTimeTicks({
	chartWidth,
	tickFontSize,
	timeValues,
	interval,
	xTickFormat,
}: GenerateTimeTicksParams): GenerateTimeTicksResult {
	// Convert time values to Date objects and find extent
	const dates = timeValues.map((d) => dayjs(d).toDate());
	const extent = d3.extent(dates) as [Date, Date] | [undefined, undefined];

	if (!extent[0] || !extent[1]) {
		return {
			ticks: [],
			suggestedInterval: 'day',
			suggestedRotation: 0,
		};
	}

	const [startDate, endDate] = extent;
	const timeRangeMs = endDate.getTime() - startDate.getTime();
	const timeRangeDays = timeRangeMs / (24 * 60 * 60 * 1000);

	// Character width estimation for different intervals
	const avgCharWidth = tickFontSize * 0.6;
	const labelWidth = xTickFormat(extent[0]).length * avgCharWidth;

	// D3 time intervals - using base intervals to ensure every() support
	const timeIntervals: Record<
		Exclude<TimeInterval, 'auto'>,
		d3.TimeInterval
	> = {
		day: d3.timeDay,
		week: d3.timeWeek,
		month: d3.timeMonth,
		quarter: d3.timeMonth.every(3)!,
		year: d3.timeYear,
	};

	// Determine best interval based on time range
	const getBestInterval = (): Exclude<TimeInterval, 'auto'> => {
		if (timeRangeDays <= 365) return 'day';
		if (timeRangeDays <= 730) return 'month'; // ~2 years
		if (timeRangeDays <= 1460) return 'quarter'; // ~4 years
		return 'year';
	};

	// Calculate how many ticks can fit based on label width and rotation
	const getMaxTicksByWidth = (
		intervalType: Exclude<TimeInterval, 'auto'>,
		rotation: 0 | 45
	): number => {
		const spacing = avgCharWidth * 4; // Space between labels

		let effectiveWidth: number;
		if (rotation === 45) {
			// When rotated 45 degrees, labels take less horizontal space
			// Approximate the horizontal projection of rotated text
			effectiveWidth = labelWidth * Math.cos(Math.PI / 4); // cos(45°) ≈ 0.707
		} else {
			effectiveWidth = labelWidth;
		}

		const maxTicks = Math.floor(chartWidth / (effectiveWidth + spacing));
		const res = Math.max(2, Math.min(18, maxTicks));

		return res;
	};

	// Generate ticks for given interval with optimal rotation
	const generateTicksWithRotation = (
		intervalType: Exclude<TimeInterval, 'auto'>
	): { ticks: Date[]; rotation: 0 | 45 } => {
		const actualInterval = timeIntervals[intervalType];

		// Generate all possible ticks in the range
		const allTicks = actualInterval.range(startDate, endDate);

		if (allTicks[allTicks.length - 1] < actualInterval.floor(endDate)) {
			allTicks.push(actualInterval.floor(endDate));
		}

		// Try to fit all ticks without rotation first
		const maxTicksNoRotation = getMaxTicksByWidth(intervalType, 0);
		if (allTicks.length <= maxTicksNoRotation) {
			return { ticks: allTicks, rotation: 0 };
		}

		// Try to fit all ticks with 45-degree rotation
		const maxTicksWithRotation = getMaxTicksByWidth(intervalType, 45);
		if (allTicks.length <= maxTicksWithRotation) {
			return { ticks: allTicks, rotation: 45 };
		}

		// Need to downsample - prefer rotation to fit more ticks
		const maxTicks = Math.max(maxTicksNoRotation, maxTicksWithRotation);
		const useRotation = maxTicksWithRotation > maxTicksNoRotation;

		// Calculate step for downsampling
		const step = Math.ceil(allTicks.length / maxTicks);

		const ticks = actualInterval.range(
			startDate,
			actualInterval.offset(endDate, 1),
			step
		);

		return { ticks, rotation: useRotation ? 45 : 0 };
	};

	// Use specified interval or determine the best one
	const targetInterval = interval !== 'auto' ? interval : getBestInterval();
	const result = generateTicksWithRotation(targetInterval);

	return {
		ticks: result.ticks,
		suggestedInterval: targetInterval,
		suggestedRotation: result.rotation,
	};
}
